/**
 * Turning an eligibility outcome into `stepData['01-01-CONTRAT']` (DEV-7682, ST7).
 *
 * `services/api/endpoints/eligibilityEndpoints.ts` owns the REQUEST (one body, one call
 * site). This module owns the other half: what the RESPONSE — or the HTTP status of a
 * failure — writes into the contract screen's step data, so the screens that consume it
 * (`02-00-GARANTIE` reads `noBenefitsFound` / `eligibilityServiceUnavailable`, the
 * beneficiary screens read `eligibilityResponse.beneficiaries`) see the same keys whoever
 * made the call.
 *
 * There are three callers of the eligibility API: the first call on `01-01-CONTRAT`, the
 * claimant-triggered relaunch on `02-00-GARANTIE`, and — since ST7 — the re-run triggered
 * by CHANGING the event date on `00-03-EVENEMENT`. Each carried its own inline copy of
 * this mapping; since ST8 all three go through this module, so a new status can only ever
 * be handled once.
 *
 * Status mapping, unchanged from those call sites:
 *  | status      | meaning                     | flags                            |
 *  |-------------|-----------------------------|----------------------------------|
 *  | 404         | contract has no coverage    | `noBenefitsFound: true`          |
 *  | 500/503/... | service down / unknown error| `eligibilityServiceUnavailable`  |
 *
 * `eligibilityServiceUnavailable` is what BLOCKS the claimant on `01-01-CONTRAT` since ST8:
 * an outage stops the flow there, while a 404 is an ANSWER and moves on to the dead end
 * `02-00-GARANTIE` already owns.
 *
 * WHAT IS NOT HERE — "a call is in flight" (DEV-8026). An `eligibilityLoading` key used to
 * be written into the step data autosave sends to NOVA, so a claim interrupted
 * mid-verification resumed as permanently loading. It is screen-local state now; ST8 makes
 * that possible, since `01-01-CONTRAT` awaits the verdict before navigating.
 *
 * PII (ADR-039): the response carries `beneficiaries`. It is stored in the in-memory store
 * and NEVER logged — callers log the HTTP status only.
 */

import type { EligibilityVerifyResponse } from '../services/api/endpoints/eligibilityEndpoints';

/** The eligibility-derived slice of `stepData['01-01-CONTRAT']`. */
export interface EligibilityStepData {
  eligibilityResponse: EligibilityVerifyResponse | { benefits: [] };
  productType?: EligibilityVerifyResponse['productType'];
  benefits: EligibilityVerifyResponse['benefits'];
  hasMedicalCoverage: boolean;
  hasCancellationCoverage: boolean;
  hasBaggageCoverage: boolean;
  eligibilityServiceUnavailable: boolean;
  noBenefitsFound: boolean;
}

/**
 * `stepData` without the legacy loading flag — what the claim payloads send (DEV-8026).
 *
 * The verdict keys are NOT touched: ST8 blocks the flow on `eligibilityServiceUnavailable`,
 * and that block has to survive a resume.
 */
export const withoutEligibilityLoading = <T extends Record<string, unknown>>(stepData: T): T => {
  const contractData = stepData?.['01-01-CONTRAT'] as Record<string, unknown> | undefined;

  if (!contractData || !('eligibilityLoading' in contractData)) return stepData;

  const keptContractData = { ...contractData };
  delete keptContractData.eligibilityLoading;

  return { ...stepData, '01-01-CONTRAT': keptContractData } as T;
};

/**
 * Has an eligibility call ANSWERED for this contract? (DEV-8026)
 *
 * Three shapes count as an answer — a response, a 404, an outage — and this module writes
 * all three, which is why the question lives here rather than in the screen that asks it:
 * a fourth outcome is added beside its builder, and `02-00-GARANTIE` inherits it.
 *
 * The two flags are redundant against `eligibilityFailureStepData`, which records a
 * non-null `eligibilityResponse`. They are read for drafts saved BEFORE DEV-7682, which
 * could hold a failure flag with no response at all.
 */
export const hasEligibilityVerdict = (
  contractData: Record<string, unknown> | null | undefined
): boolean =>
  Boolean(
    contractData
      && (contractData.eligibilityResponse != null
        || contractData.noBenefitsFound
        || contractData.eligibilityServiceUnavailable)
  );

/**
 * Clears the previous verdict without erasing the contract — written before a call starts.
 *
 * Carries no "loading" flag (DEV-8026): the spinner is the calling screen's own state, so
 * an interrupted call can no longer be persisted as a permanent one.
 */
export const eligibilityClearedStepData = (): Pick<
  EligibilityStepData,
  'eligibilityServiceUnavailable' | 'noBenefitsFound'
> => ({
  eligibilityServiceUnavailable: false,
  noBenefitsFound: false
});

/** Successful verification. */
export const eligibilitySuccessStepData = (
  eligibility: EligibilityVerifyResponse
): EligibilityStepData => ({
  eligibilityResponse: eligibility,
  productType: eligibility.productType,
  benefits: eligibility.benefits,
  hasMedicalCoverage: Boolean(eligibility.hasMedicalCoverage),
  hasCancellationCoverage: Boolean(eligibility.hasCancellationCoverage),
  hasBaggageCoverage: Boolean(eligibility.hasBaggageCoverage),
  eligibilityServiceUnavailable: false,
  noBenefitsFound: false
});

/**
 * Failed verification, branched on the HTTP status.
 *
 * @param httpStatus `error.response?.status`, `undefined` for a network error / timeout.
 */
export const eligibilityFailureStepData = (httpStatus?: number): EligibilityStepData => ({
  eligibilityResponse: { benefits: [] },
  benefits: [],
  hasMedicalCoverage: false,
  hasCancellationCoverage: false,
  hasBaggageCoverage: false,
  // 404 is an ANSWER ("this contract covers nothing that day"), not an outage.
  eligibilityServiceUnavailable: httpStatus !== 404,
  noBenefitsFound: httpStatus === 404
});
